/**
 * Pure helpers for the Planning V1 UI (SOLY-PLAN-001). No React, no I/O — unit-tested.
 */
import type {
  ScheduleEventKind, DurationUnit, SuiviProjectGroupDto, ScheduleEventDto, UnavailabilityDto,
  EmployeeSkillType, EmployeeDto, WorkItemStatus,
} from '../../../lib/api/v2/planning'
// Single source of truth for the FR function labels (SOLY-TEAM-001 vocabulary): the agenda must
// never drift from what the Équipe page displays.
import { functionLabel } from '../admin/team-helpers'
import { planningSettings } from './planning-settings'
import { formatDate } from '../../../lib/format'

/** FR label for an event kind (UI is French; codes are English). */
export function kindLabel(kind: ScheduleEventKind): string {
  switch (kind) {
    case 'Installation': return 'Pose'
    case 'TechnicalSurvey': return 'Métré'
    case 'Appointment': return 'RDV'
    case 'InternalWork': return 'Interne'
  }
}

/**
 * Saturated accent per kind — legend dots (design tokens, _design.md round 6).
 *
 * These are the SAME colours the bars wear, expressed as CSS variables rather than as hexes.
 * They used to be literals frozen before the Tailwind v4 upgrade (`#2563EB` for the pose), while
 * the bar beside them renders `bg-blue-600`, which v4 resolves from oklch to a different blue.
 * The legend was therefore teaching a colour the grid does not use — two blues in one row, one
 * inch apart. Reading the variable means the dot cannot drift from the bar again, whatever the
 * palette does next.
 */
export function kindColor(kind: ScheduleEventKind): string {
  switch (kind) {
    case 'Installation': return 'var(--color-blue-600)'
    case 'TechnicalSurvey': return 'var(--color-violet-600)'
    case 'Appointment': return 'var(--color-gray-400)'
    // Cooler and lighter than the RDV grey: an RDV concerns someone outside the company, internal
    // work engages nobody but us — it must recede one notch further.
    case 'InternalWork': return 'var(--color-slate-300)'
  }
}

/**
 * Event bar classes per kind — « V3 cockpit » direction (validated 2026-06-10): solid saturated
 * fills for the métier work (scannable from across the room — full vs empty days must read
 * instantly), light dashed neutral for free RDV so they recede. Supersedes the pastel round-6 try
 * (contrast too weak to scan density).
 */
export function kindClasses(kind: ScheduleEventKind): string {
  switch (kind) {
    // Blue stays here on purpose (brand alignment 2026-08-29): in this map blue is a CATEGORY,
    // not an action. Ink would collide with the two slate neutrals right below it and kill the
    // density read the solid fills exist for.
    case 'Installation': return 'bg-blue-600 text-white'
    case 'TechnicalSurvey': return 'bg-violet-600 text-white'
    // slate-200, not slate-100. The RDV is the one kind whose PALE state cannot go paler —
    // slate-50 IS --color-background — so the fill delta has to come from the confirmed side.
    // At slate-100 the two states were 1.05:1 apart and the proposé bar was the LOUDER of the
    // two (it alone wears the 4px edge): the grammar was inverted on the very kind SOLY-PLAN-002
    // exists for. slate-200 puts real weight on « acté » and keeps the RDV recessive against a
    // saturated pose.
    case 'Appointment': return 'bg-slate-200 text-slate-800 border border-dashed border-slate-400'
    // NO dashed border: the dash is the RDV's identity, and two discreet neutral kinds side by side
    // must not be confusable. Internal work is flat, plain, and a shade cooler.
    case 'InternalWork': return 'bg-slate-200/70 text-slate-600 border border-slate-300'
  }
}

/**
 * Lane grouping order (Thierry 2026-08-11): the agenda answers « où caser la pose ? » first, so
 * Placeurs open the grid, Métreurs follow, Commerciaux close it. A member holding several functions
 * is filed under the highest-ranked one — one person, one lane.
 */
export const LANE_FUNCTION_ORDER: readonly EmployeeSkillType[] = ['Installer', 'Surveyor', 'Commercial']

/** The member's functions, ranked — drives both the grouping key and the lane sub-label. */
export function orderedFunctions(skills: readonly EmployeeSkillType[]): EmployeeSkillType[] {
  return LANE_FUNCTION_ORDER.filter((fn) => skills.includes(fn))
}

/** The function a member is filed under, or `null` when they hold none. */
export function primaryFunction(skills: readonly EmployeeSkillType[]): EmployeeSkillType | null {
  return orderedFunctions(skills)[0] ?? null
}

/** FR sub-label under a lane's name, e.g. « Placeur · Métreur ». Empty when no function is set. */
export function functionsLabel(skills: readonly EmployeeSkillType[]): string {
  return orderedFunctions(skills).map(functionLabel).join(' · ')
}

/**
 * Keeps only the assignees who can still receive work — décision Thierry 2026-09-10: « un employé
 * désactivé ne peut pas accepter une nouvelle assignation ».
 *
 * Why this exists at all: the agenda deliberately keeps a LANE for a deactivated member who still
 * holds past events, so their history does not vanish from the grid. Clicking an empty cell in that
 * lane, or adding a créneau to a block they were on, used to carry them into the NEW slot — and
 * nothing said no. The server refuses it now (ASSIGNEE_NOT_ASSIGNABLE); this is the half that stops
 * the user reaching a refusal they could not see coming.
 *
 * Only for CREATION. Editing an existing event must keep the crew it already has, deactivated or
 * not, or a departure would strand every job that person was on.
 */
export function assignableOnly(
  candidateUserIds: readonly string[],
  employees: readonly Pick<EmployeeDto, 'userId' | 'isActive'>[],
): string[] {
  const active = new Set(employees.filter((e) => e.isActive).map((e) => e.userId))
  return candidateUserIds.filter((id) => active.has(id))
}

/**
 * The lane's sub-label. Deactivated members are called out: they sit below every function group
 * (they are not staff to plan against), so a bare « Placeur » sitting under the Commerciaux would
 * read as a sorting bug. Empty only for an active member holding no function.
 */
export function laneSubLabel(skills: readonly EmployeeSkillType[], isActive: boolean): string {
  const functions = functionsLabel(skills)
  if (isActive) return functions
  return functions ? `${functions} · désactivé` : 'désactivé'
}

/**
 * Agenda lane order: active members grouped by function (Placeur → Métreur → Commercial, then those
 * with no function), alphabetical inside each group. Deactivated members sink to the bottom — they
 * only own a lane because a past event still points at them (SOLY-TEAM-001), they are not staff to
 * plan against. Stable and pure → unit-tested. Does not mutate the input.
 */
export function sortEmployeeLanes<T extends Pick<EmployeeDto, 'displayName' | 'email' | 'skills' | 'isActive'>>(
  employees: readonly T[],
): T[] {
  const rank = (e: T) => {
    const fn = primaryFunction(e.skills)
    return fn === null ? LANE_FUNCTION_ORDER.length : LANE_FUNCTION_ORDER.indexOf(fn)
  }
  return [...employees].sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1
    const byFunction = rank(a) - rank(b)
    if (byFunction !== 0) return byFunction
    return (a.displayName || a.email).localeCompare(b.displayName || b.email, 'fr-BE')
  })
}

/**
 * How the agenda groups its lanes. Persisted per user; « crew » only offered once a crew exists.
 * « type » reads the agenda by kind of work: poses, then métrés, then RDV.
 */
export type AgendaLaneMode = 'type' | 'person' | 'crew'

/** One row of the agenda body. `memberIds` is what an event is matched against. */
export interface AgendaLane {
  /** Stable React key AND testid suffix. Crew lanes are deliberately NOT `planning-row-` (see below). */
  key: string
  kind: 'unassigned' | 'crew' | 'person' | 'type'
  label: string
  /** Second line under the label: the functions of a person, the members of a crew. */
  sublabel: string
  /**
   * Whose events land on this lane. Empty for the « Non assigné » tray, which matches events with no
   * assignment at all rather than a set of people.
   */
  memberIds: string[]
  /**
   * The subset of `memberIds` that is still active. **This is the population every decision about
   * the lane is taken on** — its shape (crew row or person row), whether it carries gestures, and
   * the « 1/2 » fraction.
   *
   * It has to be, because `memberIds` is not stable: the roster the agenda is handed holds a
   * deactivated member only while an event still points at them IN THE LOADED WINDOW. Deciding
   * anything on the full set made the same crew read as a read-only crew row one week and as a
   * person row the next, and the same bar read « 1/2 » then « 1/1 ». Two rules over two populations
   * is what produced both.
   */
  activeMemberIds: string[]
  /** Set on `type` lanes only: the event kind whose slots land here, assigned or not. */
  eventKind?: ScheduleEventKind
}

const UNASSIGNED_LANE = 'unassigned'

/**
 * Which grouping the agenda opens on. SOLY-PLAN-005 D5.
 *
 * Two rules in one place, because they only make sense together:
 *   — with no crew in the org there is nothing to group, so « person » whatever is stored. An org
 *     that creates none never sees a pixel change, and the toggle is not even rendered.
 *   — with a crew, « crew » is the default: creating one IS the statement of intent. A stored
 *     choice wins over it, and anything unreadable in storage falls back to the default rather than
 *     leaving the grid in a third state.
 */
export function resolveLaneMode(stored: string | null, hasCrews: boolean): AgendaLaneMode {
  // The type view needs no crew, so a stored choice of it stands on its own. It is never the
  // DEFAULT: nobody lands on it without having picked it. « project » is the lens it replaced
  // (SOLY-PLAN-008, staging only): whoever picked that one asked for this one.
  if (stored === 'type' || stored === 'project') return 'type'
  if (!hasCrews) return 'person'
  return stored === 'person' || stored === 'crew' ? stored : 'crew'
}

/**
 * The agenda's rows, for a given grouping mode. SOLY-PLAN-005.
 *
 * « Non assigné » OPENS the grid in both modes (Élodie 2026-08-18): it is not a person, it is the
 * arrival tray. Then, in `crew` mode, one row per crew that has at least TWO active members,
 * followed by everyone else on their own row — people in no crew, and the members of a crew that
 * has nothing to group, who keep their own row with the crew name added under their name.
 *
 * A crew row exists only when it has something to group. That is not cosmetic: a crew row is
 * READ-ONLY (D1 — the target of a ＋ would be ambiguous), so rendering one active person as a crew
 * took away their ＋, their double-click and their absence button. That was the first defect of
 * this feature, and it came back through a second door when the collapse counted ALL members while
 * the badge counted the ACTIVE ones. One population, one rule.
 *
 * ⚠️ Crew lane keys are `crew:<id>`, and the grid renders them as `planning-lane-crew-<id>`, NEVER
 * with the `planning-row-` prefix: TC-019 and TC-031 derive a USER id from the first
 * `[data-testid^="planning-row-"]` that is not « unassigned », so a crew sharing that prefix would
 * hand them a crew id and fail somewhere else entirely.
 */
export function buildAgendaLanes(
  employees: readonly EmployeeDto[],
  mode: Exclude<AgendaLaneMode, 'type'>,
): AgendaLane[] {
  const tray: AgendaLane = {
    key: UNASSIGNED_LANE,
    kind: 'unassigned',
    label: 'Non assigné',
    sublabel: '',
    memberIds: [],
    activeMemberIds: [],
  }

  /**
   * `extraSublabel` is PREPENDED, never substituted: the lane order is built on functions, so a row
   * that stopped displaying its function would be filed by a criterion it no longer shows — and the
   * « désactivé » marker would vanish from the one row where it explains why the row is sunk.
   */
  const personLane = (e: EmployeeDto, extraSublabel?: string): AgendaLane => {
    const own = laneSubLabel(e.skills, e.isActive)
    return {
      key: e.userId,
      kind: 'person',
      label: e.displayName || e.email,
      sublabel: [extraSublabel, own].filter(Boolean).join(' · '),
      memberIds: [e.userId],
      activeMemberIds: e.isActive ? [e.userId] : [],
    }
  }

  const ordered = sortEmployeeLanes(employees)
  if (mode === 'person') {
    return [tray, ...ordered.map((e) => personLane(e))]
  }

  // Group in the SORTED order so a crew's members are listed the way the person view lists them,
  // and so two crews never swap places between renders.
  const crews = new Map<string, { name: string; members: EmployeeDto[] }>()
  const loners: EmployeeDto[] = []
  for (const e of ordered) {
    if (e.crewId) {
      const bucket = crews.get(e.crewId)
      if (bucket) bucket.members.push(e)
      else crews.set(e.crewId, { name: e.crewName || 'Équipe', members: [e] })
    } else {
      loners.push(e)
    }
  }

  const crewLanes: AgendaLane[] = []
  /** Everyone who ends up on their own row, with the crew name to show when they have one. */
  const asPeople: { employee: EmployeeDto; crewName?: string }[] = loners.map((e) => ({ employee: e }))

  for (const [crewId, crew] of crews) {
    const active = crew.members.filter((m) => m.isActive)
    if (active.length < 2) {
      for (const m of crew.members) {
        asPeople.push({ employee: m, crewName: crew.name })
      }
      continue
    }

    crewLanes.push({
      key: `crew:${crewId}`,
      kind: 'crew',
      label: crew.name,
      // The names ARE the sub-label: with the row named after the crew, this is the only place that
      // answers « who is in it ? » without a click. A deactivated member is called out here too —
      // they are on the row because an event still points at them, not because they are staff.
      sublabel: crew.members
        .map((m) => (m.isActive ? m.displayName || m.email : `${m.displayName || m.email} (désactivé)`))
        .join(' · '),
      memberIds: crew.members.map((m) => m.userId),
      activeMemberIds: active.map((m) => m.userId),
    })
  }

  crewLanes.sort((a, b) => a.label.localeCompare(b.label, 'fr-BE'))

  // The people are re-sorted as ONE block, so someone whose crew had nothing to group lands where
  // the person view would put them — a deactivated member sunk to the bottom, rather than floated
  // to the top by the crew ordering.
  const crewNameOf = new Map(asPeople.map((p) => [p.employee.userId, p.crewName]))
  const peopleLanes = sortEmployeeLanes(asPeople.map((p) => p.employee))
    .map((e) => personLane(e, crewNameOf.get(e.userId)))

  return [tray, ...crewLanes, ...peopleLanes]
}

/** The kinds the « Types » lanes are built from, in reading order. */
const TYPE_LANE_ORDER: readonly { kind: ScheduleEventKind; label: string; always: boolean }[] = [
  // Interventions (finitions, retouches) are poses too: they stay on this lane, the amber fill
  // already tells them apart.
  { kind: 'Installation', label: 'Poses', always: true },
  { kind: 'TechnicalSurvey', label: 'Métrés', always: true },
  { kind: 'Appointment', label: 'Rendez-vous', always: true },
  // Internal work is neither a RDV nor a chantier step. Folding it into « Rendez-vous » would
  // make that lane lie; it gets its own last lane, drawn only when some is on screen.
  { kind: 'InternalWork', label: 'Interne', always: false },
]

/**
 * The agenda's rows in « Types » mode (Kitchy, 2026-10-06): every pose on the first lane, every
 * métré on the second, every RDV on the third. It replaces the « Projets » lens (SOLY-PLAN-008),
 * which answered « what happens in which dossier » when the question was « what is planned, by
 * kind of work ».
 *
 * Unlike the person and crew views, an event lives on exactly ONE lane here — its kind's — so a
 * shared chantier is never drawn twice. Who works on it moves from the lane onto the bar.
 *
 * The three main lanes are always drawn, even empty: « nothing to measure this week » is an
 * answer, and lanes that came and went would move the RDV row from one week to the next.
 *
 * ⚠️ Keys are `type:<kind>`; the grid must never render them under the `planning-row-` testid
 * prefix (TC-019 / TC-031 read a USER id off the first such testid).
 */
export function buildTypeLanes(events: readonly ScheduleEventDto[], days: readonly Date[]): AgendaLane[] {
  const onScreen = new Set(events.filter((e) => days.some((d) => eventOnDay(e, d))).map((e) => e.kind))
  return TYPE_LANE_ORDER
    .filter((t) => t.always || onScreen.has(t.kind))
    .map((t) => ({
      key: `type:${t.kind}`, kind: 'type', label: t.label, sublabel: '',
      memberIds: [], activeMemberIds: [], eventKind: t.kind,
    }))
}

/**
 * « Karim, Sam +1 » — who works on a bar, short enough for a 120px cell (SOLY-PLAN-008 2.9: the
 * names may be abbreviated). Used by the « Types » lanes, where the lane no longer says
 * who. First names only; two people sharing one in the same bar get the
 * initial of their last name (« Sam D. », « Sam L. ») so the bar never names two people alike.
 * Two names at most, then « +N ». The full names stay available in the bar's tooltip.
 */
export function abbreviateAssignees(userIds: readonly string[], nameById: ReadonlyMap<string, string>): string {
  if (userIds.length === 0) return 'non assigné'
  const parts = userIds.map((id) => {
    const full = (nameById.get(id) ?? '').trim()
    // A member with no display name is shown by their e-mail: keep its local part.
    const words = full.split(/\s+/).filter(Boolean).map((w) => (w.includes('@') ? w.split('@')[0] : w))
    const first = words[0] || '?'
    const initial = words.length > 1 ? `${words[words.length - 1][0]}.` : ''
    return { first, initial }
  })
  const count = new Map<string, number>()
  for (const p of parts) count.set(p.first, (count.get(p.first) ?? 0) + 1)
  const shown = parts.map((p) => ((count.get(p.first) ?? 0) > 1 && p.initial ? `${p.first} ${p.initial}` : p.first))
  return shown.length <= 2 ? shown.join(', ') : `${shown.slice(0, 2).join(', ')} +${shown.length - 2}`
}

/** Every assignee's full name, for the tooltip of a « Types » bar whose last line abbreviates them. */
export function assigneeFullNames(ev: ScheduleEventDto, nameById: ReadonlyMap<string, string>): string {
  return ev.assignments.map((a) => nameById.get(a.assignedUserId) ?? '?').join(', ')
}

/**
 * Which slots carry a « Proposé / Confirmé » state.
 *
 * SOLY-PLAN-002 opens it to the RDV — the secretary's actual question. It stays closed on internal
 * work, which has no counterparty: a « à confirmer » badge nobody will ever clear is how the signal
 * dies of noise. Listing the kinds rather than negating one keeps a future 5th kind out until
 * someone decides.
 */
export function kindHasConfirmation(kind: ScheduleEventKind): boolean {
  return kind === 'Installation' || kind === 'TechnicalSurvey' || kind === 'Appointment'
}

/**
 * The statuses that SETTLE a work item's créneaux — the single home of the « terminal states » rule
 * this file used to write inline, and `ProjectDetail` used to write twice more.
 *
 * 🔴 It is NOT the same list for the two work items, since SOLY-PLAN-006 (décision D3).
 *
 * On a POSE the report closes the job: a second constat there is a FINITION, with its own work item
 * and its own report, so `Completed` settles.
 *
 * On a MÉTRÉ it does not. A métré can be visited again after its report — « avec ou sans rapport,
 * on doit pouvoir mettre un autre créneau » — so `Completed` now means only « a report has been
 * entered ». A FUTURE créneau on a reported métré must therefore still read « Proposé » and still
 * be confirmable; reading it as settled would put « clôturé le … » on the agenda for a passage that
 * has not happened yet.
 *
 * The comment on the old inline version warned that this rule lived in THREE places and that they
 * « agree by review, not by construction ». This lot is the moment they stop agreeing, so the rule
 * is written once here and the three call sites read it.
 */
export function isWorkItemSettled(
  kind: ScheduleEventKind,
  status: WorkItemStatus | null | undefined,
): boolean {
  // Phrased as « is one of the terminal ones » rather than « is not one of the live ones », so an
  // unknown status (a backend one deployment behind) reads as LIVE: the agenda keeps its previous
  // behaviour rather than silently going quiet on a live question.
  if (status === 'Cancelled' || status === 'NotApplicable') return true
  // The métré's exception, and the whole point of this function existing.
  return status === 'Completed' && kind !== 'TechnicalSurvey'
}

/**
 * Whether the « Proposé / Confirmé » question is still worth asking about this slot.
 *
 * It ends when the WORK ITEM DIES, never when the slot's end date goes by — the reading that looks
 * obvious and is wrong. A RDV the client never confirmed is a RDV that did not happen: the day
 * after it, the secretary still has to confirm it or move it, and an agenda that stopped asking
 * would be telling her it took place (Thierry, 2026-09-01, from Kitchy's secretary). Once the work
 * item is closed or cancelled, the job is settled and the question is moot.
 *
 * Not « a report was recorded » either: cancelling a sale leaves the pose Cancelled with its PAST
 * créneaux still on the agenda, and those must stop asking too. And on a métré, a report does not
 * settle anything at all — see {@link isWorkItemSettled}.
 *
 * RDV and internal work have no work item at all, so they stay confirmable indefinitely — which is
 * exactly the intent.
 */
export function isConfirmationLive(event: Pick<ScheduleEventDto, 'kind'> & { workItemStatus?: WorkItemStatus | null }): boolean {
  if (!kindHasConfirmation(event.kind)) return false
  return !isWorkItemSettled(event.kind, event.workItemStatus)
}

/**
 * What a settled slot says instead of « Proposé » / « Confirmé ».
 *
 * Three terminal states, three different words, because « Clôturé » is only true of one of them:
 * the drawer already says « Pose annulée » and « non nécessaire » for the other two, and
 * MarkNotApplicable does NOT cancel the work item's créneaux — so a bar saying « Clôturé » can sit
 * on the agenda next to a drawer saying « non nécessaire », about a job nothing ever closed.
 *
 * Returns null while the question is still live.
 */
export function settledLabel(
  kind: ScheduleEventKind,
  status: WorkItemStatus | null | undefined,
  closedAt: string | null | undefined,
): string | null {
  // 🔴 A reported MÉTRÉ is not settled (SOLY-PLAN-006, décision D3): it can still be visited
  // again, so its créneaux keep asking « Proposé / Confirmé ». Without this guard the bar of a
  // FUTURE passage on a reported métré would read « clôturé le … » — and, since MS-07 makes
  // ReportSummaryAt move to the last amendment, « clôturé le 18/09 » on a créneau of the 8th.
  if (!isWorkItemSettled(kind, status)) return null

  switch (status) {
    case 'Completed': return closedAt ? `clôturé le ${formatDate(closedAt)}` : 'clôturé'
    case 'Cancelled': return 'annulé'
    case 'NotApplicable': return 'non nécessaire'
    default: return null
  }
}

/**
 * Kinds whose free title IS their identity on the bar (« Montage Dupont », « Rencontrer M. Dupont »).
 * Mirrors the BE `ScheduleEventKinds.HasFreeLabel`. A métré/pose takes its title from the chantier.
 *
 * This predicate exists because `kind === 'Appointment'` was scattered over the agenda, and none of
 * those tests would have failed to COMPILE with a 4th kind — they would simply have treated internal
 * work as « pas un RDV » and dropped its title. That is the silent failure mode of this lot.
 */
export function kindHasFreeLabel(kind: ScheduleEventKind): boolean {
  return kind === 'Appointment' || kind === 'InternalWork'
}

/**
 * Kinds with no work-item anchor — the two the user creates freely on the agenda, and the two ends
 * of the reclassement corridor (BE `ScheduleEvent.ConvertTo`). Also the kinds allowed on any day.
 */
export function isFreeKind(kind: ScheduleEventKind): boolean {
  return kind === 'Appointment' || kind === 'InternalWork'
}

/**
 * True when this kind's SOLID bar is painted on a LIGHT fill — the question the bar's secondary
 * text colour depends on.
 *
 * Deliberately phrased about luminosity rather than about `kind === 'Appointment'`: the whole point
 * of the lot that introduced it was that a 4th kind slipped through every `=== 'Appointment'` test
 * without the compiler noticing. A 5th light-filled kind must land here by answering the question,
 * not by being remembered. (A finition re-tints a pose to amber-600 — still dark.)
 */
export function kindHasLightFill(kind: ScheduleEventKind): boolean {
  return kind === 'Appointment' || kind === 'InternalWork'
}

/**
 * The bar of a confirmable slot still awaiting its confirmation: the kind's colour, PALE, with the
 * saturated hue kept on a 4px left edge.
 *
 * It replaces a diagonal hatch (Elodie 2026-06-14). The hatch was white stripes at 30 % opacity
 * over a saturated fill, and on a 24px bar it was not a difference, it was a suspicion — the whole
 * team said, unanimously, that they could not tell « à confirmer » from « confirmé ». Fill weight
 * is the standard calendar idiom for it: pale = proposé, plein = acté.
 *
 * The left edge is what protects the round-6 decision the solid fills exist for — scanning a full
 * day from an empty one across the room. A pale bar still lands a saturated stripe in the column.
 *
 * Contrast is deliberate, not incidental, and every figure below was recomputed by two independent
 * reviewers rather than eyeballed — three of the numbers first written here were wrong.
 * Main text on its own pale fill: blue-900/blue-50 9.5:1, violet 10.0:1, amber 8.7:1, slate 9.9:1.
 * Secondary line, in the darkest step of its OWN hue: blue-700 6.3:1, violet-700 6.7:1,
 * amber-700 4.9:1, and slate-600 7.2:1 for the RDV, which has no hue of its own.
 *
 * A REPLACEMENT of `kindClasses`, like `natureTint`: Tailwind conflicts are not resolved by class
 * order, so the two must never both reach the element.
 */
export function tentativeClasses(kind: ScheduleEventKind, isIntervention = false): string {
  if (kind === 'Installation') {
    return isIntervention
      ? 'bg-amber-50 text-amber-900 border-l-4 border-l-amber-600'
      : 'bg-blue-50 text-blue-900 border-l-4 border-l-blue-600'
  }
  switch (kind) {
    case 'TechnicalSurvey': return 'bg-violet-50 text-violet-900 border-l-4 border-l-violet-600'
    // The RDV keeps its dashed edge in BOTH states — the dash is its identity against internal
    // work, not its confirmation state.
    //
    // It is the ONE kind whose pale fill cannot carry the signal: slate-50 IS --color-background
    // (rgb(248 250 252)), so a bg-slate-50 bar is painted in the page's own colour — exactly
    // 1.00:1, an invisible bar. Caught in review before it shipped, and it would have hit the
    // exact case this work is about: the RDV nobody confirmed.
    //
    // So the 4px left edge carries this kind, and the dashed boundary is slate-400. Being honest
    // about that boundary: at 2.5:1 it does NOT reach the 3:1 WCAG 1.4.11 asks of a component
    // boundary — an earlier version of this comment cited that threshold as the reason to pick
    // slate-400 over slate-300 (1.4:1), which was hollow, since neither passes. What passes is the
    // EDGE: slate-500 on slate-50 is 4.6:1, and it is the edge that says « this is a RDV, and it
    // is not settled yet ».
    case 'Appointment': return 'bg-slate-50 text-slate-700 border border-dashed border-slate-400 border-l-4 border-l-slate-500'
    // Never reached (internal work is not confirmable); listed so a 5th kind must answer here.
    case 'InternalWork': return kindClasses(kind)
  }
}

/**
 * The secondary line of a tentative bar, in the darkest readable step of the kind's OWN hue —
 * slate-600 for the RDV, which has no hue of its own.
 *
 * Separate from the fill because the bar's second line asks a different question of the palette:
 * it must stay readable at 10px against a tinted ground, where a grey borrowed from another
 * palette reads as a rendering accident.
 */
export function tentativeMutedText(kind: ScheduleEventKind, isIntervention = false): string {
  if (kind === 'Installation') return isIntervention ? 'text-amber-700' : 'text-blue-700'
  // slate-600, not slate-500: the 500 step lands at 4.55:1 on slate-50, which passes AA by one
  // percent and made the comment above false for the one kind that has no hue of its own.
  return kind === 'TechnicalSurvey' ? 'text-violet-700' : 'text-slate-600'
}

/** Monday of the week containing `d` (UTC, ISO week start). */
export function startOfWeek(d: Date): Date {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const day = date.getUTCDay() // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day
  date.setUTCDate(date.getUTCDate() + diff)
  return date
}

/** ISO-8601 week number (1-53) of the week containing `d` — week 1 is the week with the first Thursday. */
export function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const dayNum = (date.getUTCDay() + 6) % 7 // Mon=0..Sun=6
  date.setUTCDate(date.getUTCDate() - dayNum + 3) // Thursday of this ISO week
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4))
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3)
  return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000))
}

/** N working days (Mon-Fri) starting at `start`, as Date objects at UTC midnight. */
export function workingDays(start: Date, count: number): Date[] {
  const days: Date[] = []
  const cursor = new Date(start)
  while (days.length < count) {
    const dow = cursor.getUTCDay()
    if (dow !== 0 && dow !== 6) {
      days.push(new Date(cursor))
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return days
}

/**
 * Agenda columns (round 6): Monday→Saturday every week — Saturday is displayed because RDV may be
 * planned there (commercials work Saturdays) — plus a Sunday column only when an event actually
 * lands on it (otherwise a Sunday RDV would exist but be invisible). Pure → unit-tested.
 */
export function agendaDays(start: Date, weekCount: number, occupiedSundays?: Set<string>): Date[] {
  const days: Date[] = []
  const cursor = new Date(startOfWeek(start))
  for (let i = 0; i < weekCount * 7; i++) {
    const dow = cursor.getUTCDay()
    if (dow !== 0 || occupiedSundays?.has(isoDate(cursor))) days.push(new Date(cursor))
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return days
}

/** True when the UTC day is a Saturday or Sunday (visual "non ouvré" tint in the grid). */
export function isWeekendDay(d: Date): boolean {
  const dow = d.getUTCDay()
  return dow === 0 || dow === 6
}

/** yyyy-MM-dd in UTC. */
export function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/**
 * Today's calendar date in Brussels as yyyy-MM-dd — the agenda's "today" reference. UTC would flag
 * the wrong column between midnight and 1/2am Brussels. `now` injectable for deterministic tests.
 */
export function brusselsTodayIso(now: Date = new Date()): string {
  return now.toLocaleDateString('en-CA', { timeZone: 'Europe/Brussels' })
}

/**
 * « Brussels now » as the two calendar values a slot is compared with — the day (« yyyy-MM-dd »)
 * and the wall clock (« HH:mm »). The browser's own zone plays no part. `now` injectable for tests.
 */
export function brusselsNow(now: Date = new Date()): { date: string; time: string } {
  const time = now.toLocaleTimeString('en-GB', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
  return { date: brusselsTodayIso(now), time }
}

/**
 * True when the slot occupies the given agenda column (a UTC-midnight Date, see agendaDays).
 *
 * SOLY-PLAN-007: a slot carries the days it occupies — `startDate` to `endDate`, both INCLUSIVE —
 * so this is a comparison of « yyyy-MM-dd » strings and nothing else. It used to overlap two
 * instants with the UTC day, which put an hourly slot at 00:00+02:00 on the column BEFORE its own
 * too (the « 0h - 3h » row of 2026-08-11) and made the bucket depend on an offset.
 */
export function eventOnDay(slot: Pick<ScheduleEventDto, 'startDate' | 'endDate'>, day: Date): boolean {
  const d = isoDate(day)
  return slot.startDate <= d && d <= slot.endDate
}

/** Minute of the day of a « HH:mm » wall clock — 8h30 → 510. NaN when it is not one. */
function wallMinutes(time: string): number {
  const m = /^(\d{2}):(\d{2})/.exec(time)
  return m ? Number(m[1]) * 60 + Number(m[2]) : Number.NaN
}

/** « 8h », « 9h30 » — a minute of the day as the agenda writes it. */
function clockLabel(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const min = minutes % 60
  return min === 0 ? `${h}h` : `${h}h${min.toString().padStart(2, '0')}`
}

/**
 * Time-of-day label for an hourly slot, e.g. "8h - 12h" or "9h30 - 11h": the wall clock the slot
 * carries, and that clock plus its duration. No instant, so no offset to subtract — an 8h RDV
 * typed in January reads 8h. Empty when the slot has no time.
 */
export function hourLabel(startTime: string | null, durationHours: number): string {
  const start = startTime ? wallMinutes(startTime) : Number.NaN
  if (Number.isNaN(start)) return ''
  // A slot ending on the stroke of midnight reads « 0h », as the instant it replaced did.
  return `${clockLabel(start)} - ${clockLabel((start + Math.round(durationHours * 60)) % (24 * 60))}`
}

/**
 * Whether the slot is OVER at Brussels `now` — mirrors the backend's `ScheduleEvent.HasEnded`
 * (SOLY-PLAN-007): an hourly slot with a time ends at that time plus its duration; any other slot
 * (a Days slot, an hourly slot whose time is not fixed) ends with its last day, `endDate`.
 *
 * Deliberately not « no longer upcoming » (rule D8, a question about the START): a pose started
 * yesterday and still running, or a métré under way, has not ended — and reading it as past is
 * what would offer « Terminer + rapport » in the middle of it. A slot without a time never ends
 * before its day does, so it is not « À clôturer » at 15h on its own day.
 */
export function slotHasEnded(
  slot: Pick<ScheduleEventDto, 'durationUnit' | 'durationValue' | 'startTime' | 'endDate'>,
  now: Date = new Date(),
): boolean {
  const { date: today, time } = brusselsNow(now)
  if (today !== slot.endDate) return today > slot.endDate
  const start = slot.durationUnit === 'Hours' && slot.startTime ? wallMinutes(slot.startTime) : Number.NaN
  if (Number.isNaN(start)) return false
  return start + Math.round(slot.durationValue * 60) < wallMinutes(time)
}

/** « 16/06/2026 » straight off a « yyyy-MM-dd » — a calendar day has no zone to be shifted by. */
export function formatCalendarDay(date: string): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`
}

/**
 * The kind, spelled out, for a search result (SOLY-PLAN-008). NOT `kindLabel`: the agenda's
 * « RDV » / « Interne » are written for a 24px bar and a legend, and a search result has the room
 * to say what the thing is. `kindLabel` stays as it is — the agenda, the fiche and the history
 * all read it.
 */
export function searchKindLabel(kind: ScheduleEventKind): string {
  switch (kind) {
    case 'Appointment': return 'Rendez-vous'
    case 'InternalWork': return 'Travail interne'
    default: return kindLabel(kind)
  }
}

/**
 * « le 03/10 à 14h » (hour-unit) or « le 03/10 » (day-unit, or an hourly slot whose time is not
 * fixed) for an event search result.
 *
 * Read off the slot's calendar day and wall clock (SOLY-PLAN-007), exactly like the agenda bar it
 * jumps to — never off an instant, which would need an offset and could disagree with the bar.
 */
export function eventDateLabel(ev: Pick<ScheduleEventDto, 'startDate' | 'startTime' | 'durationUnit'>): string {
  const date = `le ${ev.startDate.slice(8, 10)}/${ev.startDate.slice(5, 7)}`
  if (ev.durationUnit === 'Days' || !ev.startTime) return date
  const minutes = wallMinutes(ev.startTime)
  return Number.isNaN(minutes) ? date : `${date} à ${clockLabel(minutes)}`
}

/**
 * Human « when » for a scheduled event: « 16/06/2026 · 8h - 10h » (Hours) or the date, plus
 * « · N j » when it spans several days.
 *
 * Lives here since SOLY-PLAN-003 MS-11 — the cockpit's Date row and the créneau card must read a
 * date the same way, and the second one is in another file. It was local to ProjectDetail before.
 */
export function formatEventWhen(ev: Pick<ScheduleEventDto, 'startDate' | 'startTime' | 'durationUnit' | 'durationValue'>): string {
  const date = formatCalendarDay(ev.startDate)
  if (ev.durationUnit === 'Hours') return `${date} · ${hourLabel(ev.startTime, ev.durationValue)}`
  return ev.durationValue > 1 ? `${date} · ${ev.durationValue} j` : date
}

/** Heatmap opacity (0..1) for a given event count: 0 → transparent, 5+ → full. */
export function densityOpacity(count: number): number {
  if (count <= 0) return 0
  return Math.min(1, 0.2 + (count / 5) * 0.8)
}

/**
 * Start time (« HH:mm ») to prefill when editing an existing slot: the wall clock it carries
 * (`startTime`, SOLY-PLAN-007 — there is no instant left to read it from).
 *
 * The ONE value that is not preserved is midnight. An hourly slot at 00:00 is never a human choice:
 * it is the broken anchor the « 0h - 3h » poses carried, and the backend refuses it outright
 * (ScheduleEvent.ValidateDuration). Handing it back to the field would let the form re-submit
 * exactly the value that produced the bug, so it falls back to the start of the work day. Every
 * OTHER time is somebody's decision — 18h30 included — and is kept as it is.
 *
 * (This used to CLAMP to a list of offered hours, because a <select> whose value matches no
 * <option> silently displays the first one while React still holds the original. An
 * <input type="time"> has no such trap: it shows what it is given.)
 */
export function startTimeOf(slot: Pick<ScheduleEventDto, 'startTime'>): string {
  const fallback = `${String(planningSettings().workDayStartHour).padStart(2, '0')}:00`
  const time = slot.startTime?.slice(0, 5)
  return !time || time === '00:00' ? fallback : time
}

/**
 * Default placement prefill for a kind — the scheduling fiche (MS-18) lets the user adjust.
 * Pose → 1 jour ; métré → 2 h dès l'ouverture ; RDV → 1 h en milieu de matinée.
 *
 * SOLY-PLAN-003 MS-05: the start times are DERIVED from the org work day, not written here — with
 * the current 7h→16h day the values are unchanged (métré 7h… RDV 9h). They are a PREFILL: since
 * 2026-08-18 nothing stops the user typing 18h30 over them.
 */
export function prefillForKind(kind: ScheduleEventKind): { unit: DurationUnit; value: number; startTime: string } {
  const { workDayStartHour, workDayEndHour } = planningSettings()
  // A RDV is not booked at opening time — two hours in, and never at or past the hour the day ends:
  // a slot STARTING at closing time already runs past it, and the fiche would open flagged « hors
  // des heures de travail », the app disagreeing with its own prefill.
  const midMorning = Math.min(workDayStartHour + 2, Math.max(workDayStartHour, workDayEndHour - 1))
  // Days slots anchor at midnight by convention, so the time is inert here; it is kept coherent
  // anyway, since flipping the fiche to Hours reads this value.
  if (kind === 'Installation') return { unit: 'Days', value: 1, startTime: hhmm(workDayStartHour) }
  if (kind === 'TechnicalSurvey') return { unit: 'Hours', value: 2, startTime: hhmm(workDayStartHour) }
  // RDV and Interne share the light default. Internal work is OFTEN multi-day (montage atelier), but
  // defaulting to a whole day would book one on every click; switching to Jours is one tap.
  return { unit: 'Hours', value: 1, startTime: hhmm(midMorning) }
}

/** A whole hour as the « HH:mm » an <input type="time"> reads and writes. */
function hhmm(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`
}

/**
 * The start a form sends (SOLY-PLAN-007): the day, plus the wall clock for an hourly slot — none
 * for Days, whose time field is inert. It replaces the instant that used to be recomposed here with
 * a hardcoded +02:00, one hour off every winter.
 *
 * `time` is the « HH:mm » an <input type="time"> produces.
 */
export function slotStart(dayIso: string, unit: DurationUnit, time: string): { startDate: string; startTime: string | null } {
  return { startDate: dayIso, startTime: unit === 'Hours' ? time : null }
}

/** True when `time` is a wall clock this app can place — « HH:mm », and not the midnight anchor. */
export function isPlaceableTime(time: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(time) && time !== '00:00'
}

/**
 * True when an hourly slot starting at `time` for `hours` would run past midnight — which the domain
 * refuses (ScheduleEvent.ValidateDuration: « an hourly event must stay within a single day »).
 *
 * Mirrored here since SOLY-PLAN-004 because the free time field makes « 22h + 3 h » a two-keystroke
 * mistake, and the backend's refusal reaches the user as raw English. The domain stays the judge;
 * this only says the same thing sooner, in French.
 */
export function spillsPastMidnight(time: string, hours: number): boolean {
  const m = /^(\d{2}):(\d{2})$/.exec(time)
  if (!m || !(hours > 0)) return false
  return Number(m[1]) * 60 + Number(m[2]) + hours * 60 > 24 * 60
}

/** Breathing room kept between the scheduling fiche and the edges of the window. */
export const FICHE_MARGIN = 8

/**
 * Where the scheduling fiche must sit so that its BOTTOM stays on screen.
 *
 * The position used to reserve a hardcoded height — 360px, then 470px after the first time the
 * submit fell below the fold. SOLY-PLAN-004 made it fall again: the note became a two-row textarea,
 * it now renders for every kind (not just the free ones), and an out-of-hours notice can add a
 * line. A magic number cannot survive a form whose height depends on its content, so the caller
 * MEASURES the rendered box (see SchedulingFiche) and hands the result here.
 *
 * `height` is the fiche's ACTUAL rendered height. Taller than the window? Pin it to the top margin:
 * the element scrolls internally (max-h + overflow-y-auto), and pinning is what keeps the FIRST
 * fields reachable rather than pushing them off the top.
 *
 * Pure → unit-tested, which a `style={{ top: … }}` expression never was.
 */
export function clampFicheTop(y: number, height: number, viewportHeight: number): number {
  const lowestTop = viewportHeight - height - FICHE_MARGIN
  return Math.max(FICHE_MARGIN, Math.min(y, lowestTop))
}

/**
 * Order of the bars inside one agenda cell (Élodie 2026-08-18 : « dans l'ordre de l'heure de
 * début »). Before this, only the Days/Hours split was sorted and two RDV of the same day came out
 * in whatever order the API listed them — 14h could sit above 8h.
 *
 * Two keys, in this order:
 *  1. Days before Hours. This is NOT a chronological claim: a Days bar is the cell's BASE and the
 *     hour bars are rendered « posées dessus » (MS-19). Flow order IS stacking order in a cell, so
 *     an hour bar sorted above a day block would break that reading, whatever the clock says.
 *  2. then the start the slot actually carries — the calendar DAY for a Days bar (a pose started
 *     two days ago sits above one starting today), the wall-clock TIME for an hour bar.
 *
 * `id` closes the sort so two slots on the same minute keep a stable order between refreshes.
 * Pure and total → unit-tested. Does not mutate the input.
 */
export function sortCellEvents<T extends Pick<ScheduleEventDto, 'id' | 'durationUnit' | 'startDate' | 'startTime'>>(
  events: readonly T[],
): T[] {
  const rank = (e: T) => (e.durationUnit === 'Days' ? 0 : 1)
  const minutes = (e: T) => {
    const m = e.startTime ? wallMinutes(e.startTime) : Number.NaN
    return Number.isNaN(m) ? 0 : m
  }
  return [...events].sort((a, b) => {
    const byUnit = rank(a) - rank(b)
    if (byUnit !== 0) return byUnit
    if (a.durationUnit === 'Days') {
      const byDay = a.startDate.localeCompare(b.startDate)
      if (byDay !== 0) return byDay
    } else {
      const byTime = minutes(a) - minutes(b)
      if (byTime !== 0) return byTime
    }
    return a.id.localeCompare(b.id)
  })
}

/**
 * Position of a given working day within an event's working-day span — the "Jx/N" label on a Day
 * event (cockpit UX finding #7a). Counts Mon–Fri days only.
 *
 * A single working day answers `{1, 1}` since Élodie's 2026-08-18 feedback. It used to answer
 * `null` to avoid the "J1/1 noise", and the bar fell back to the word « Journée » — but reading a
 * column then meant switching between two grammars for the same fact, and « J1/1 » is the one that
 * says how much of the pose this block is. The word is gone; only a day OUTSIDE the span still
 * answers `null` (the Saturday column of a pose running Friday→Monday), because inventing a
 * position for it would be a lie.
 *
 * `startDate` / `endDate` are the slot's calendar days, both INCLUSIVE (SOLY-PLAN-007).
 *
 * Pure → unit-tested.
 */
export function dayPositionInSpan(startDate: string, endDate: string, day: Date): { index: number; total: number } | null {
  const isWorking = (d: Date) => d.getUTCDay() !== 0 && d.getUTCDay() !== 6
  // Walk the calendar days from the first to the last, collecting the working ones. The Date is
  // only a day counter anchored at UTC midnight — no instant of the slot is ever built.
  const span: string[] = []
  const cursor = new Date(`${startDate}T00:00:00Z`)
  // Guard against pathological inputs.
  let guard = 0
  while (isoDate(cursor) <= endDate && guard < 366) {
    if (isWorking(cursor)) span.push(isoDate(cursor))
    cursor.setUTCDate(cursor.getUTCDate() + 1)
    guard += 1
  }
  const idx = span.indexOf(isoDate(day))
  if (idx < 0) return null
  return { index: idx + 1, total: span.length }
}

/**
 * How pressing the requested install date is — drives the colour of the inbox card date (never
 * whether it is displayed: the date is the sort key of its own list, it is always shown).
 */
export type InstallDateUrgency = 'overdue' | 'soon' | 'normal'

export interface RequestedInstallLabel {
  /** Compact FR label for a ~330px column, e.g. « Pose 12/09 ». */
  text: string
  urgency: InstallDateUrgency
}

/** Days ahead under which a requested install date reads as urgent (historic inbox threshold). */
const URGENT_WINDOW_DAYS = 14

/**
 * Compact "requested install date" label for an inbox card (fr-BE dd/MM, year only when the date
 * falls outside the current civil year — the column is narrow). Returns `null` when there is no
 * usable date: the card then shows nothing at all (product decision — no placeholder).
 *
 * The date part is read straight off the ISO string rather than through `new Date()` so the day the
 * user picked is the day displayed, whatever the browser timezone (the API sends Brussels offsets).
 * Urgency is computed in calendar days against the Brussels today, which reproduces the historic
 * millisecond threshold for midnight-anchored dates. Pure → unit-tested, `now` injectable.
 */
export function formatRequestedInstall(iso: string | null, now: Date = new Date()): RequestedInstallLabel | null {
  if (!iso) return null
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!parts) return null
  const [, year, month, day] = parts
  const today = brusselsTodayIso(now)
  const daysAhead = Math.round((Date.parse(`${year}-${month}-${day}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / (24 * 60 * 60 * 1000))

  let urgency: InstallDateUrgency = 'normal'
  if (daysAhead < 0) urgency = 'overdue'
  else if (daysAhead <= URGENT_WINDOW_DAYS) urgency = 'soon'

  const sameYear = year === today.slice(0, 4)
  const text = sameYear ? `Pose ${day}/${month}` : `Pose ${day}/${month}/${year.slice(2)}`
  return { text, urgency }
}

/**
 * Pose capacity of one working day (V3 cockpit, validated 2026-06-10). Counts INSTALLERS only —
 * the question this answers is « où caser la pose ? ». Honest by construction:
 *   absent (congé/maladie) → leaves the denominator · occupied = ≥1 whole-day (Days) task ·
 *   partial = hour tasks only (taken, not blocked) · no hour-level workload, no travel time.
 */
export interface DayCapacity { available: number; occupied: number; partial: number; absent: number }

export function poseCapacityForDay(
  day: Date,
  installerIds: string[],
  events: ScheduleEventDto[],
  unavailabilities: UnavailabilityDto[],
): DayCapacity {
  const cap: DayCapacity = { available: 0, occupied: 0, partial: 0, absent: 0 }
  for (const id of installerIds) {
    if (unavailabilities.some((u) => u.userId === id && eventOnDay(u, day))) {
      cap.absent += 1
      continue
    }
    cap.available += 1
    const todays = events.filter(
      (e) => e.assignments.some((a) => a.assignedUserId === id) && eventOnDay(e, day),
    )
    if (todays.some((e) => e.durationUnit === 'Days')) cap.occupied += 1
    else if (todays.length > 0) cap.partial += 1
  }
  return cap
}

/**
 * Placeur-days actually consumed — « plein = tâche journée, demi = heures » (`_design.md` round
 * 6bis). SOLY-FIX-004: the gauge SEGMENTS drew that rule from day one, but the figures beside them
 * counted only `occupied`, so a week genuinely full of hourly poses read « 0/10 j · 0 % ». The bar
 * and the number under it must tell the same story, and this is that story, in one place.
 */
export function poseLoadOf(cap: DayCapacity): number {
  return cap.occupied + cap.partial * 0.5
}

/** A load as fr-BE text: « 2 », « 0,5 », « 1,5 ». Never a rounded « 0 » that hides half a day. */
export function formatPoseLoad(load: number): string {
  return load.toLocaleString('fr-BE', { maximumFractionDigits: 1 })
}

/** Open alert points carried by one work item of a project (drives the agenda bar badge). */
export interface AlertBadge { count: number; hard: boolean }
export interface ProjectAlertBadges { metre?: AlertBadge; pose?: AlertBadge }

/**
 * Maps the Suivi alerts onto the work item whose agenda bar carries the attention badge. The BE's
 * ConstraintDefinition declares the carrier (`workItem`) — _constraints.md invariant 2 — so this is
 * a straight routing, no code-number pattern-matching (M1 « métré non planifié » badges the POSE
 * bar by definition: the métré has no bar). Red when any Hard point, amber otherwise. Pure → unit-tested.
 */
export function alertBadgesByProject(groups: SuiviProjectGroupDto[]): Record<string, ProjectAlertBadges> {
  const out: Record<string, ProjectAlertBadges> = {}
  for (const g of groups) {
    const badges: ProjectAlertBadges = {}
    for (const a of g.alerts) {
      const target = a.workItem === 'TechnicalSurvey' ? 'metre' : 'pose'
      const b = badges[target] ?? { count: 0, hard: false }
      badges[target] = { count: b.count + 1, hard: b.hard || a.severity === 'Hard' }
    }
    out[g.projectId] = badges
  }
  return out
}

/** Map a work-item status to a stepper glyph. */
export function statusGlyph(status: string): string {
  switch (status) {
    case 'Open': return '○'
    case 'NotScheduled': return '○' // legacy strings (pre mini-scope 28) — kept for any stale caller
    case 'Scheduled': return '◐'
    case 'InProgress': return '◐'
    case 'Completed': return '✓'
    case 'NotApplicable': return '⊘'
    case 'Cancelled': return '✗'
    default: return '○'
  }
}
